import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../../pixiSettings";
import { SOUND_NAME } from "../../../../sounds";
import anime from "animejs/lib/anime.es.js";
import FreeSpinMaxWinController from "./FreeSpinMaxWinController";
import { safeInit } from "../../../../utilities";
import { selectShowFreeSpinMaxWin } from "../../../../State/Slices/animationsSlice";
import {
  selectShowHoldAndWinMaxWin,
  selectHoldAndWinWin,
  setShowHoldAndWinMaxWin,
} from "../../../../State/Slices/holdAndWinSlice";
import { freeSpinOutroBet } from "../../../../Games/Shared/freeSpinOutroPopup";
import { soundManager } from "../../../../SoundManager";

/**
 * FreeSpinMaxWinDialog — the win cap, hit during the free-spin feature.
 *
 * The `max_win` skin of the shared win_celebration spine. A SEPARATE component
 * from PotCollectionMaxWinDialog on purpose, so the free-spin version can
 * diverge without disturbing Zeus. Same shape as the other popups:
 *
 *   - The component NEVER unmounts. Visibility is alpha, which is what gives
 *     the spine's `out` a chance to play before the flag clears.
 *   - The overlay fades through `overlayRef`, driven by the controller.
 *   - Closing is the controller's job: `out`, then the flag, then the fade.
 */

const OVERLAY_BACKGROUND_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const OVERLAY_BACKGROUND_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;
const OVERLAY_FADE_DURATION = POPUP_OVERLAY_CONFIG.FADE_DURATION;

// variant "hold_and_win" — the same popup for Hold & Win's capped ending
// (2026-10-01, Pot Collection's Max Win swap): its own flag, H&W's paid
// amount, its own close flag; the close hands over to the same wipe.
const FreeSpinMaxWinDialog = ({ variant = "free_spins" }) => {
  const isHoldAndWin = variant === "hold_and_win";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // const show = useSelector(selectShowFreeSpinMaxWin);
  const showFreeSpins = useSelector(selectShowFreeSpinMaxWin);
  const showHoldAndWin = useSelector(selectShowHoldAndWinMaxWin);
  const show = isHoldAndWin ? showHoldAndWin : showFreeSpins;
  // ── The FREE-SPIN round's total, not the base game's spin ──
  //
  // This read `state.game.currentRound.win` — copied from the base-game max
  // win dialog, where that IS the round. In here it is the TRIGGER spin, which
  // has usually paid nothing, so the popup opened on a counter of 0 and a line
  // reading "0x BET" while the player had just won the cap.
  //
  // Never caught because the popup is unreachable without a 30000x round.
  // FreeSpinTotalWinDialog and FreeSpinZeroWinDialog both read this field, and
  // it is the figure the cap is enforced against — see capRoundTotal in the
  // PopiPlay RGS, which clamps it to bet x MAX_WIN_MULTIPLIER.
  // const totalWin = useSelector((state) => state.freeSpins?.totalWin ?? 0);
  const freeSpinsTotal = useSelector((state) => state.freeSpins?.totalWin ?? 0);
  // Hold & Win: what the settling respin PAID (capped).
  const holdAndWinTotal = useSelector(selectHoldAndWinWin);
  const totalWin = isHoldAndWin ? holdAndWinTotal : freeSpinsTotal;

  // ── ONE denominator for every outro popup ──
  //
  // The stake the round was PLAYED at, not the price and not the live bet.
  // This was three inline copies of the same rule, one per popup, while the
  // threshold that CHOOSES between them used a fourth. freeSpinOutroPopup owns
  // it now, and carries the full reasoning plus the two wrong sources that
  // were tried first.
  const effectiveBet = useSelector(freeSpinOutroBet);

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const { dimensions, uiSymbolHeight: uiSymbolSize, device } = dimensionsContext;

  const [controllerReady, setControllerReady] = useState(false);
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          if (typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
        complete: () => {
          setOverlayAlpha(0);
          if (typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once — it lives for the session.
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new FreeSpinMaxWinController(
          containerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
          ...(isHoldAndWin ? [setShowHoldAndWinMaxWin] : []),
        );
        // ── Rendered, so the effect below can depend on it ──
        //
        // A ref is invisible to React, so keyed on [show] alone the start
        // effect fires once against a null ref and never runs again — `show`
        // does not change a second time. A completion reload does exactly
        // that: the round is already over in the store before this component
        // mounts, so the dialog is asked to open on the first frame and the
        // player is left in the bonus room with no popup and no wipe.
        //
        // FreeSpinTotalWinDialog carries this flag already; this one and the
        // zero-win dialog did not, because neither was reachable.
        setControllerReady(true);
      });
    }
    return () => setControllerReady(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    controllerRef.current?.updateDimensions(dimensionsContext, uiSymbolSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // The win and the bet it was made at, pushed in before the sequence starts —
  // the same order PotCollectionMaxWinDialog uses, so the value is on screen
  // from the first frame rather than popping in.
  useEffect(() => {
    if (controllerReady && controllerRef.current && show) {
      controllerRef.current.setTotalWinValue(totalWin, effectiveBet);
      controllerRef.current.startSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, controllerReady]);

  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt?.preventDefault) evt.preventDefault();
      if (evt?.stopPropagation) evt.stopPropagation();
      if (!controllerRef.current || !show) return;
      if (controllerRef.current.handleUserClick()) {
        soundManager.play(SOUND_NAME.tap_to_continue);
      }
    },
    [show],
  );

  // Spacebar dismisses too, matching the intro.
  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (controllerRef.current?.handleUserClick()) {
        soundManager.play(SOUND_NAME.tap_to_continue);
      }
    };

    document.addEventListener("keydown", handleKeyDown, {
      capture: true,
      passive: false,
    });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [show]);

  useEffect(() => {
    return () => {
      controllerRef.current?.cleanup();
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  const containerPosition = useMemo(
    () => ({ x: dimensions.width / 2, y: dimensions.height / 2 }),
    [dimensions.width, dimensions.height],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="free_spin_max_win"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={containerRef}
      // Gates the WHOLE subtree. The tap-to-continue container is eventMode
      // "static" with its own hit area, and closing only drops the spine's
      // alpha — so without this it stays invisible but still swallowing
      // pointer events, for every popup, for ever. Every Zeus popup carries
      // this guard; these five were missing it.
      eventMode={show ? "auto" : "none"}
    >
      {/* Drawn back to 0,0 so the rect covers the screen while the spine
          stays centred. */}
      <Graphics
        name="free_spin_max_win_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={show ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        // Clickable, but NEVER a pointer — only the tap word offers one.
        cursor="default"
      />
      {/* Spine, label and tap-to-continue are owned by the controller and
          pinned into the spine's own slots. */}
    </Container>
  );
};

export default memo(FreeSpinMaxWinDialog);
